import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { RESET_PASSWORD_ERROR_CODE } from "common/constants";
import { formatCollection } from "common/utils/strings";
import {
  ResetPasswordError,
  useGetResetRequestQuery,
  useResetPasswordMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import {
  translatePasswordValidationErrors,
  validatePasswordsMatch,
  validatePasswordWithTranslations,
} from "../../../utils/passwords";
import { CenterContent } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import FullPageLoadingScreen from "../../Common/FullPageLoadingScreen";
import { Section, Sections } from "../../Common/Layout";
import { Title } from "../../Common/Typography";
import { Body } from "../../Common/Typography";
import { Text } from "../../Inputs/react-hook-form";
import { Container } from "../CommonLoginComponents/__styles__/FormWithFullPageBackground";

const ExpiredRequestMessage = () => {
  const { t } = useTranslation();
  return (
    <Container>
      <Section>
        <Title type="semiBold" size="large">
          {t("password-reset-expired-header")}
        </Title>
        <Body size="default" type="regular">
          {t("password-reset-expired-text")}
        </Body>
      </Section>
    </Container>
  );
};

const SuccessMessage = ({ onLoginClick }: { onLoginClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Container>
      <Sections>
        <Section>
          <Title type="semiBold" size="large">
            {t("success-title")}
          </Title>
          <Body size="default" type="regular">
            {t("password-reset-success-message")}
          </Body>
        </Section>
        <Button
          onClick={onLoginClick}
          size="medium"
          styleVariant="primary"
          stretch
        >
          {t("common-log-in")}
        </Button>
      </Sections>
    </Container>
  );
};

interface FormStructure {
  password: string;
  confirmPassword: string;
}

export const ResetForm = ({
  minimumPasswordLength,
  requestId,
  onLoginClick,
}: ResetFormURLParams & {
  minimumPasswordLength: number;
  onLoginClick: () => void;
}) => {
  const { addErrorToast } = useStatusToasts();
  const { t } = useTranslation();

  const [completed, setCompleted] = React.useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    setError,
  } = useForm<FormStructure>({});

  const handlePasswordResetError = (error?: Maybe<ResetPasswordError>) => {
    let fieldError = null;
    switch (error?.code) {
      case RESET_PASSWORD_ERROR_CODE.NEW_PASSWORD_SAME_AS_OLD_PASSWORD:
        fieldError = t("password-must-be-different-from-old");
        break;
      case RESET_PASSWORD_ERROR_CODE.PASSWORD_MISSING_REQUIREMENTS:
        fieldError = t("password-must-have", {
          parts: formatCollection(
            translatePasswordValidationErrors({
              errors: error.passwordValidationErrors!,
              minimumPasswordLength,
              t,
            }),
          ),
        });
        break;
      case RESET_PASSWORD_ERROR_CODE.PASSWORD_REQUIRED:
        fieldError = t("password-is-required");
        break;
      case RESET_PASSWORD_ERROR_CODE.SSO_USER:
        addErrorToast(t("sso-password-reset-not-available"));
        break;
      default:
        addErrorToast(t("password-reset-error-message"));
        break;
    }

    if (fieldError) {
      setError("password", { message: fieldError }, { shouldFocus: true });
    }
  };

  const [resetPassword, { loading }] = useResetPasswordMutation({
    onCompleted: ({ completePasswordReset }) => {
      if (completePasswordReset?.ok) {
        setCompleted(true);
      } else {
        handlePasswordResetError(completePasswordReset?.error);
      }
    },
    onError: () => {
      addErrorToast(t("password-reset-error-message"));
    },
  });

  if (completed) {
    return <SuccessMessage onLoginClick={onLoginClick} />;
  }

  return (
    <Container>
      <Sections
        as="form"
        onSubmit={handleSubmit(async userProvidedData => {
          await resetPassword({
            variables: { requestId, password: userProvidedData.password },
          });
        })}
      >
        <Section>
          <Title type="semiBold" size="large">
            {t("password-reset-form-title")}
          </Title>
          <Text
            label={t("password-reset-form-label")}
            type="password"
            data-testid="password"
            error={errors.password?.message}
            {...register("password", {
              required: t("common-required-field"),
              validate: (password: Maybe<string>) => {
                return validatePasswordWithTranslations({
                  password,
                  minimumPasswordLength,
                  t,
                });
              },
            })}
          />
          <Text
            label={t("password-reset-form-confirm-label")}
            type="password"
            data-testid="confirm-password"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword", {
              required: t("common-required-field"),
              validate: (val: Maybe<string>) => {
                return validatePasswordsMatch({
                  password: val,
                  confirmPassword: getValues("password"),
                  t,
                });
              },
            })}
          />
        </Section>
        <Button
          styleVariant="primary"
          size="medium"
          onClick={handleSubmit(async userProvidedData => {
            await resetPassword({
              variables: { requestId, password: userProvidedData.password },
            });
          })}
          disabled={loading}
          stretch
        >
          {t("common-save")}
        </Button>
      </Sections>
    </Container>
  );
};

export interface ResetFormURLParams {
  requestId: string;
}

export interface ResetFormProps extends RouteComponentProps<ResetFormURLParams> {
  onLoginClick: () => void;
}

export default ({ match, onLoginClick }: ResetFormProps) => {
  const requestId = match.params.requestId;

  const { data, loading, error } = useGetResetRequestQuery({
    variables: { requestId },
  });

  if (loading) return <FullPageLoadingScreen />;

  if (error || !data) {
    throw new Error(
      `Could not find password reset request with id: ${requestId}`,
    );
  }

  const expired = data.passwordResetRequest === null;

  return (
    <CenterContent>
      {expired ?
        <ExpiredRequestMessage />
      : <ResetForm
          requestId={requestId}
          minimumPasswordLength={
            data.passwordResetRequest!.requester.account.securityConfiguration
              .minimumPasswordLength
          }
          onLoginClick={onLoginClick}
        />
      }
    </CenterContent>
  );
};
